import EventEmitter from 'eventemitter3';
import * as PIXI from 'pixi.js';
import { HeartRateParam } from './heartRate';
import { UpDownController } from './pixiUpDown';

export interface Value {
  min: number;
  max: number;
  step: number;
  unit: string;
}

export interface Setting {
  t1: Value;
  t2: Value;
  s: Value;
}

export class RRController extends EventEmitter<{
  update: [HeartRateParam];
  tchange: [number];
}> {
  private setting: Setting;
  private container: PIXI.Container;
  private line: PIXI.Graphics;
  private valueText: PIXI.Text[] = [];
  private labelText: PIXI.Text[] = [];
  private buttons: UpDownController[] = [];
  private param?: HeartRateParam;

  constructor(setting: Setting) {
    super();
    this.container = new PIXI.Container();

    this.line = new PIXI.Graphics();
    this.setting = setting;

    const width = 110;
    const rowHeight = 30;
    this.line.moveTo(0, 0)
      .lineStyle(1, 0xffffff)
      .lineTo(width, 0)
      .lineTo(width, rowHeight * 4)
      .lineTo(0, rowHeight * 4)
      .lineTo(0, 0)
      .moveTo(0, rowHeight)
      .lineTo(width, rowHeight)
      .moveTo(0, rowHeight * 2)
      .lineTo(width, rowHeight * 2)
      .moveTo(0, rowHeight * 3)
      .lineTo(width, rowHeight * 3)
      .moveTo(18, 0)
      .lineTo(18, rowHeight * 4)
      .moveTo(width - 20, 0)
      .lineTo(width - 20, rowHeight * 4);
    this.container.addChild(this.line);
    this.container.visible = false;

    const style = new PIXI.TextStyle({
      fill: [0x5dffff],
      fontSize: 13,
    });
    const unit = [this.setting.t1.unit, this.setting.t2.unit, this.setting.s.unit];
    for (let i = 0; i < 3; i += 1) {
      const text = new PIXI.Text(`${this.setting.t1}${unit[i]}`, style);
      this.valueText.push(text);
      text.position = new PIXI.Point(18, rowHeight * (i + 1) + 8);
      this.container.addChild(text);
    }
    const label = ['T', 'T1', 'T2', 'S'];
    for (let i = 0; i < 4; i += 1) {
      const text = new PIXI.Text(label[i], style);
      this.labelText.push(text);
      text.position = new PIXI.Point(1, rowHeight * i + 8);
      this.container.addChild(text);
    }

    this.buttons.push(new UpDownController(15, rowHeight - 5));
    this.buttons.push(new UpDownController(15, rowHeight - 5));
    this.buttons.push(new UpDownController(15, rowHeight - 5));
    this.buttons.push(new UpDownController(15, rowHeight - 5));

    this.container.addChild(...this.buttons.map((v) => v.getContainer()));

    this.buttons[0].getContainer().position = new PIXI.Point(width - 18, 2);
    this.buttons[1].getContainer().position = new PIXI.Point(width - 18, rowHeight + 2);
    this.buttons[2].getContainer().position = new PIXI.Point(width - 18, rowHeight * 2 + 2);
    this.buttons[3].getContainer().position = new PIXI.Point(width - 18, rowHeight * 3 + 2);

    this.buttons[0].on('change', (value) => {
      this.emit('tchange', value);
    });
    this.buttons[1].on('change', (value) => {
      if (!this.param) {
        return;
      }
      this.param.t1 += this.setting.t1.step * value;
      this.param.t1 = Math.min(this.setting.t1.max, Math.max(this.param.t1, this.setting.t1.min));
      this.valueText[0].text = `${this.param.t1}${this.setting.t1.unit}`;
      this.emit('update', this.param);
    });
    this.buttons[2].on('change', (value) => {
      if (!this.param) {
        return;
      }
      this.param.t2 += this.setting.t2.step * value;
      this.param.t2 = Math.min(this.setting.t2.max, Math.max(this.param.t2, this.setting.t2.min));
      this.valueText[1].text = `${this.param.t2}${this.setting.t1.unit}`;
      this.emit('update', this.param);
    });
    this.buttons[3].on('change', (value) => {
      if (!this.param) {
        return;
      }
      this.param.s += this.setting.s.step * value;
      this.param.s = Math.min(this.setting.s.max, Math.max(this.param.s, this.setting.s.min));
      this.valueText[2].text = `${Math.floor(this.param.s * 10 + 0.5) / 10}${this.setting.s.unit}`;
      this.emit('update', this.param);
    });
  }

  public set(param: HeartRateParam) {
    this.param = param;
    this.valueText[0].text = `${this.param.t1}${this.setting.t1.unit}`;
    this.valueText[1].text = `${this.param.t2}${this.setting.t2.unit}`;
    this.valueText[2].text = `${Math.floor(this.param.s * 10 + 0.5) / 10}${this.setting.s.unit}`;
  }

  public setRr(param: { t1: number; t2: number; s: number }) {
    if (this.param) {
      this.param.s = param.s;
      this.param.t1 = param.t1;
      this.param.t2 = param.t2;
    }
  }

  public show() {
    this.container.visible = true;
  }

  public hide() {
    this.container.visible = false;
  }

  public getContainer() {
    return this.container;
  }
}
